/**
 * https://github.com/auth0/auth0-spa-js
 * @license
 * The MIT License (MIT)
 *
 * Copyright (c) 2018 Auth0, Inc. <support@auth0.com> (http://auth0.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * PKCE S256 proof key
 */
export interface PkceS256ProofKey {
  state: string;
  nonce: string;
  codeChallenge: string;
  codeVerifier: string;
}
export interface PkceS256Secret {
  state: string;
  codeVerifier: string;
}
/**
 * Generate a proof key for PKCE.
 */
export async function generatePkceS256ProofKey(): Promise<PkceS256ProofKey> {
  const state = encode(createRandomString());
  const nonce = encode(createRandomString());
  const codeVerifier = createRandomString();
  const codeChallenge = bufferToBase64UrlEncoded(await sha256(codeVerifier));
  return { state, nonce, codeChallenge, codeVerifier };
}
function encode(value: string) {
  return btoa(value);
}
function createRandomString() {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
  let random = '';
  const randomValues = Array.from(window.crypto.getRandomValues(new Uint8Array(43)));
  randomValues.forEach((v) => (random += charset[v % charset.length]));
  return random;
}
async function sha256(s: string) {
  return window.crypto.subtle.digest({ name: 'SHA-256' }, new TextEncoder().encode(s));
}
function urlEncodeB64(input: string) {
  const b64Chars: { [index: string]: string } = { '+': '-', '/': '_', '=': '' };
  return input.replace(/[+/=]/g, (m: string) => b64Chars[m]);
}
function bufferToBase64UrlEncoded(input: ArrayBuffer) {
  const ie11SafeInput = new Uint8Array(input);
  return urlEncodeB64(window.btoa(String.fromCharCode(...Array.from(ie11SafeInput))));
}
